import { SEO } from "@/components/SEO";
import { PageSchema } from "@/components/seo/PageSchema";
import { LastUpdated } from "@/components/seo/LastUpdated";
import { TopCTA } from "@/components/cta/TopCTA";
import { MidPageCTA } from "@/components/cta/MidPageCTA";
import { BottomCTA } from "@/components/cta/BottomCTA";
import { SymptomChecker } from "@/components/content/SymptomChecker";
import { FixSteps } from "@/components/content/FixSteps";
import { DIYvsDoneForYou } from "@/components/content/DIYvsDoneForYou";
import { FAQSection } from "@/components/content/FAQSection";
import { InternalLinks } from "@/components/content/InternalLinks";
import { LeadFormSection } from "@/components/LeadFormSection";

const ServicePagesNotRanking = () => {
  const symptoms = [
    "Your service pages don't appear in Google search results",
    "Competitors rank for services you also offer",
    "Pages rank on page 3+ but can't break into top results",
    "Traffic to service pages is minimal despite good homepage traffic",
    "Google Search Console shows low impressions for service terms",
    "You're ranking for your business name but not service keywords"
  ];

  const rootCauses = [
    { cause: "Thin content on service pages", impact: "Google sees nothing unique or valuable to rank" },
    { cause: "No unique content per service", impact: "Similar pages compete with each other, diluting rankings" },
    { cause: "Missing keyword optimization", impact: "Google doesn't understand what the page is about" },
    { cause: "Poor internal linking to service pages", impact: "Pages lack authority signals from other site pages" },
    { cause: "No local relevance signals", impact: "Generic pages don't rank for 'near me' searches" },
    { cause: "Missing schema markup", impact: "Google can't categorize your services properly" },
    { cause: "Slow page load speed", impact: "Slow pages rank lower, especially on mobile" },
    { cause: "Duplicate or near-duplicate content", impact: "Google may only index one version, ignoring others" }
  ];

  const fixSteps = [
    { step: "Expand each service page to 800+ words", details: "Include what the service includes, process, pricing factors, and FAQs" },
    { step: "Optimize title tags with target keywords", details: "'Lawn Mowing Services in [City] | [Company]' format" },
    { step: "Add unique content to each service page", details: "Different problems solved, different customer types, different scenarios" },
    { step: "Create internal links from homepage and related pages", details: "Every service page should have 5+ internal links pointing to it" },
    { step: "Include local signals on each page", details: "Mention service areas, embed a map, reference local landmarks" },
    { step: "Add Service schema markup", details: "Help Google understand you offer this as a formal service" },
    { step: "Add 5-8 FAQs with schema", details: "Answer common questions and capture FAQ rich snippets in search" }
  ];

  const faqs = [
    { question: "Why aren't my service pages ranking in Google?", answer: "The most common reasons are thin content (pages with only 100-200 words), missing keyword optimization, and lack of internal links. Google needs substantial, unique content to understand what your page offers and rank it accordingly." },
    { question: "How long should service pages be?", answer: "Aim for 800-1,500 words per service page. Include service description, process explanation, what's included, pricing factors, who the service is for, and 5-8 FAQs. This gives Google enough content to understand and rank your page." },
    { question: "Should each service have its own page?", answer: "Absolutely. Separate pages for lawn mowing, fertilization, aeration, etc. allow each to rank for its specific keywords. One page listing all services won't rank for any individual service term." },
    { question: "What keywords should I target on service pages?", answer: "Target '[service name] + [location]' as your primary keyword. For example, 'lawn aeration services Columbus Ohio.' Secondary keywords include variations like 'lawn aeration near me' and 'aeration service cost.'" },
    { question: "How do I make my service pages unique from competitors?", answer: "Focus on your specific process, pricing transparency, service guarantees, customer results, and local expertise. Include before/after photos from local jobs and testimonials specific to that service." },
    { question: "Do I need schema markup on service pages?", answer: "Yes. Service schema helps Google categorize your offerings. Add LocalBusiness and Service schema to each service page. Also add FAQPage schema if you include an FAQ section (which you should)." },
    { question: "How long until my service pages start ranking?", answer: "With proper optimization, you should see movement in 4-8 weeks. Competitive keywords may take 3-6 months. Consistently adding content, earning reviews, and building links accelerates ranking improvements." },
    { question: "Should I include pricing on service pages?", answer: "Including pricing ranges (e.g., 'Lawn mowing starts at $35/visit') helps qualify leads and can improve click-through rates. Exact pricing isn't required, but transparency about cost factors improves conversions." },
    { question: "How many internal links should point to each service page?", answer: "At minimum, 5 internal links from your homepage, other service pages, blog posts, and service area pages. More internal links signal to Google that the page is important." },
    { question: "What FAQs should I include on service pages?", answer: "Answer questions about: what the service includes, how long it takes, when to schedule, cost factors, frequency recommendations, and what makes your service different. Use actual questions customers ask." }
  ];

  const links = [
    { to: "/websites/", label: "Website Subscription Pricing" },
    { to: "/service-pages-that-rank/", label: "Service Pages That Rank" },
    { to: "/lawn-care-website-seo/", label: "Lawn Care Website SEO" },
    { to: "/website-fixes/service-area-pages-not-ranking/", label: "Service Area Pages Not Ranking" }
  ];

  const CANONICAL = "https://www.leafrushmarketing.com/website-fixes/service-pages-not-ranking/";

  return (
    <>
      <SEO
        title="Service Pages Not Ranking? Here's Why & How to Fix"
        description="Are your lawn care or landscaping service pages not ranking in Google? Learn the root causes and step-by-step fixes to improve visibility."
        canonical={CANONICAL}
        ogUrl={CANONICAL}
      />
      <PageSchema
        url={CANONICAL}
        title="Service Pages Not Ranking? Here's Why & How to Fix"
        description="Are your lawn care or landscaping service pages not ranking in Google? Learn the root causes and fixes."
        faqs={faqs}
      />
      <LastUpdated />

      <article className="py-12">
        <div className="container mx-auto px-4 max-w-4xl">
          <header className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold text-foreground mb-4">
              Service Pages Not Ranking? Here's Why & How to Fix It
            </h1>
            <p className="text-xl text-muted-foreground">
              Your competitors rank for the same services you offer. Here's how to claim your spot in search results.
            </p>
          </header>

          <TopCTA />

          <SymptomChecker symptoms={symptoms} title="Signs Your Service Pages Have Ranking Problems" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">Root Causes: Why Google Ignores Your Service Pages</h2>
            <div className="grid md:grid-cols-2 gap-4">
              {rootCauses.map((item, index) => (
                <div key={index} className="p-4 bg-muted rounded-lg">
                  <h3 className="font-semibold text-foreground">{item.cause}</h3>
                  <p className="text-sm text-muted-foreground mt-1">{item.impact}</p>
                </div>
              ))}
            </div>
          </section>

          <MidPageCTA />

          <FixSteps steps={fixSteps} title="7 Steps to Get Your Service Pages Ranking" />

          <section className="my-12">
            <h2 className="text-3xl font-bold text-foreground mb-6">The Thin Content Trap</h2>
            <div className="prose prose-lg max-w-none text-foreground">
              <p>
                Most lawn care and landscaping websites have service pages that are little more than a title and a few bullet points. Google looks at these pages and sees nothing worth ranking—no depth, no unique value, no reason to show it to searchers.
              </p>
              <p>
                <strong>Robust service pages are your secret weapon for organic traffic.</strong> When you provide comprehensive information about each service, Google rewards you with rankings—and visitors trust you more, converting at higher rates.
              </p>
              <p>
                At <a href="/websites/" className="text-primary hover:underline">Leaf Rush Marketing</a>, every website includes SEO-optimized service pages with proper structure, keyword targeting, and schema markup. Our clients rank for their services, not just their business name.
              </p>
            </div>
          </section>

          <DIYvsDoneForYou />

          <FAQSection faqs={faqs} />

          <InternalLinks links={links} title="Related Resources" />

          <BottomCTA />

          <LeadFormSection title="Get Your Service Pages Ranking" />
        </div>
      </article>
    </>
  );
};

export default ServicePagesNotRanking;
